// Sales by day as a calendar strip: a bar per day beside the same weekday in the
// comparison dates, Shabbos and Yom Tov shaded and named, Hebrew dates underneath,
// and a mark under the days the comparison dates were closed for Yom Tov.
import { useEffect, useMemo, useRef, useState } from "react";
import { compactMoney, count as countText, money, shortDate as shortDay, shortRange } from "./format";
import "./SalesTrend.css";

const BAR_AREA = 172;
const METRICS = {
  sales: { label: "Sales", value: (point) => Number(point?.sales || 0), format: (value) => money(value), axis: (value) => compactMoney(value) },
  orders: { label: "Orders", value: (point) => Number(point?.orders || 0), format: (value) => countText(value), axis: (value) => countText(value) },
};
const UNIT_LABEL = { day: "day", week: "week", month: "month" };
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Spellings used in the shop rather than the ones the browser's Hebrew calendar uses.
const HEBREW_MONTHS = { Tishri: "Tishrei", Heshvan: "Cheshvan", Tamuz: "Tammuz", "Adar I": "Adar I", "Adar II": "Adar II" };

const parse = (value) => new Date(`${value}T00:00:00Z`);

let hebrewFormat;
function hebrewDate(value) {
  try {
    hebrewFormat = hebrewFormat || new Intl.DateTimeFormat("en-u-ca-hebrew", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
    const parts = hebrewFormat.formatToParts(parse(value));
    const part = (type) => parts.find((item) => item.type === type)?.value;
    const month = part("month");
    const year = part("year") || part("relatedYear");
    // Browsers without the Hebrew calendar fall back to Gregorian; skip the row then.
    if (!month || Number(year) < 5000) return null;
    return { day: Number(part("day")), month: HEBREW_MONTHS[month] || month, year };
  } catch (error) {
    return null;
  }
}

function dayLabel(value, first) {
  const date = parse(value);
  const day = date.getUTCDate();
  return { top: WEEKDAYS[date.getUTCDay()], main: first || day === 1 ? `${MONTHS[date.getUTCMonth()]} ${day}` : String(day) };
}
function periodLabel(point, granularity) {
  if (!point) return "";
  if (granularity === "month") return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(parse(point.start_date));
  if (granularity === "week" && point.start_date !== point.end_date) return shortRange(point.start_date, point.end_date);
  return `${WEEKDAYS[parse(point.start_date).getUTCDay()]} ${shortDay(point.start_date)}`;
}
function columnLabel(point, granularity, index) {
  if (granularity === "month") return { main: MONTHS[parse(point.start_date).getUTCMonth()] };
  if (granularity === "week") return { main: shortDay(point.start_date) };
  return dayLabel(point.start_date, index === 0);
}

// A rounded gridline step so the lines land on readable values.
function niceStep(range, ticks = 3) {
  const raw = range / ticks || 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * magnitude;
}

/** Runs of consecutive indexes for which pick(index) returns the same truthy value. */
function runs(length, pick) {
  const result = [];
  for (let index = 0; index < length; index += 1) {
    const value = pick(index);
    const last = result[result.length - 1];
    if (value && last && last.value === value && last.end === index - 1) last.end = index;
    else if (value) result.push({ value, start: index, end: index });
  }
  return result;
}

export default function SalesTrend({ trend, comparisonLabel, id }) {
  const [metricKey, setMetricKey] = useState("sales");
  const scrollRef = useRef(null);
  const current = trend?.current || [];
  const previous = trend?.previous || null;
  const granularity = trend?.granularity || "day";
  const metric = METRICS[metricKey];
  const daily = granularity === "day";

  const chart = useMemo(() => {
    const length = current.length;
    const values = [...current, ...(previous || [])].map(metric.value);
    const max = Math.max(1, ...values);
    const step = niceStep(max);
    const top = Math.max(step, Math.ceil(max / step) * step);
    const height = (value) => Math.max(0, Math.round((value / top) * BAR_AREA));
    const hebrew = daily ? current.map((point) => hebrewDate(point.start_date)) : [];
    const columns = current.map((point, index) => {
      const before = previous?.[index];
      const weekday = parse(point.start_date).getUTCDay();
      const closed = daily && point.holiday ? "yomtov" : daily && weekday === 6 ? "shabbos" : null;
      return {
        point, before, closed,
        name: point.holiday || (closed === "shabbos" ? "Shabbos" : ""),
        now: height(metric.value(point)),
        then: before ? height(metric.value(before)) : 0,
        label: columnLabel(point, granularity, index),
        hebrewDay: hebrew[index]?.day,
        title: `${periodLabel(point, granularity)}${point.holiday ? ` (${point.holiday})` : ""}: ${metric.format(metric.value(point))}`
          + (before ? `\n${periodLabel(before, granularity)}${before.holiday ? ` (${before.holiday})` : ""}: ${metric.format(metric.value(before))}` : ""),
      };
    });
    const hebrewMonths = hebrew.every(Boolean) && hebrew.length
      ? runs(length, (index) => `${hebrew[index].month} ${hebrew[index].year}`).map((run, index) => ({
        ...run, text: index === 0 ? run.value : run.value.replace(/ \d+$/, ""),
      })) : [];
    const closedBefore = daily && previous ? runs(length, (index) => previous[index]?.holiday || null) : [];
    return { columns, step, stepHeight: (step / top) * BAR_AREA, hebrewMonths, closedBefore, length };
  }, [current, previous, metric, granularity, daily]);

  // Long ranges scroll sideways; start at the latest days.
  useEffect(() => {
    const element = scrollRef.current;
    if (element && element.scrollWidth > element.clientWidth) element.scrollLeft = element.scrollWidth;
  }, [chart.length]);

  if (!current.length) return null;

  const unit = UNIT_LABEL[granularity];
  const currentTotal = current.reduce((sum, point) => sum + metric.value(point), 0);
  const previousTotal = previous ? previous.reduce((sum, point) => sum + metric.value(point), 0) : null;
  const best = current.reduce((winner, point) => (metric.value(point) > metric.value(winner) ? point : winner), current[0]);
  const template = { gridTemplateColumns: `repeat(${chart.length}, minmax(${daily ? 34 : 44}px, 1fr))` };
  const hasShabbos = chart.columns.some((column) => column.closed === "shabbos");
  const hasYomTov = chart.columns.some((column) => column.closed === "yomtov") || chart.closedBefore.length > 0;

  return <section className="sales-trend sa-box" id={id} aria-labelledby="sales-trend-title">
    <div className="sa-box-head">
      <div>
        <h3 className="sa-box-title" id="sales-trend-title">{metric.label} by {unit}</h3>
        <p className="sa-box-sub">{metric.value(best) > 0 ? <>Best {unit} {periodLabel(best, granularity)}, {metric.format(metric.value(best))}. </> : null}
          Lines every {metric.axis(chart.step)}.</p>
      </div>
      <div className="sales-trend-controls">
        <div className="sales-trend-legend">
          <span><i aria-hidden="true" className="sales-trend-swatch sales-trend-swatch--current" />{shortRange(current[0].start_date, current[current.length - 1].end_date)}</span>
          {previous && <span><i aria-hidden="true" className="sales-trend-swatch sales-trend-swatch--previous" />{daily ? "Same weekday, " : ""}{comparisonLabel}</span>}
          {hasYomTov && <span><i aria-hidden="true" className="sales-trend-swatch sales-trend-swatch--yomtov" />Yom Tov</span>}
          {hasShabbos && <span><i aria-hidden="true" className="sales-trend-swatch sales-trend-swatch--shabbos" />Shabbos</span>}
        </div>
        <div className="sales-trend-toggle" role="group" aria-label="Show">
          {Object.entries(METRICS).map(([key, item]) => <button type="button" key={key} aria-pressed={metricKey === key}
            className={metricKey === key ? "is-selected" : ""} onClick={() => setMetricKey(key)}>{item.label}</button>)}
        </div>
      </div>
    </div>

    <div className="sales-trend-scroll" ref={scrollRef}>
      <div className="sales-trend-strip" style={{ minWidth: chart.length * (daily ? 37 : 48) }} aria-hidden="true">
        <div className="sales-trend-columns" style={template}>
          {chart.columns.map((column) => <div key={column.point.start_date} className="sales-trend-column" title={column.title}>
            <div className={`sales-trend-bars${column.closed ? ` is-${column.closed}` : ""}`}
              style={{ height: BAR_AREA, backgroundSize: `100% ${chart.stepHeight}px` }}>
              {column.closed && <span className="sales-trend-closed">{column.name}</span>}
              <span className="sales-trend-pair">
                <i className="sales-trend-bar sales-trend-bar--current" style={{ height: column.now }} />
                {previous && <i className="sales-trend-bar sales-trend-bar--previous" style={{ height: column.then }} />}
              </span>
            </div>
            {column.label.top && <span className="sales-trend-weekday">{column.label.top}</span>}
            <span className="sales-trend-day">{column.label.main}</span>
            {column.hebrewDay != null && <span className="sales-trend-hebrew">{column.hebrewDay}</span>}
          </div>)}
        </div>
        {chart.hebrewMonths.length > 0 && <div className="sales-trend-months" style={template}>
          {chart.hebrewMonths.map((run) => <span key={run.start} style={{ gridColumn: `${run.start + 1} / ${run.end + 2}` }}>{run.text}</span>)}
        </div>}
        {chart.closedBefore.length > 0 && <div className="sales-trend-before" style={template}>
          {chart.closedBefore.map((run) => <i key={`m${run.start}`} style={{ gridColumn: `${run.start + 1} / ${run.end + 2}`, gridRow: 1 }} />)}
          {chart.closedBefore.map((run) => <span key={`t${run.start}`} style={{
            gridColumn: `${Math.max(1, run.start)} / ${Math.min(chart.length, run.end + 2) + 1}`, gridRow: 2,
          }}>{run.value}</span>)}
        </div>}
      </div>
    </div>
    {chart.closedBefore.length > 0 && <p className="sales-trend-note">Marked under the dates: days {comparisonLabel} was closed for Yom Tov.</p>}

    <div className="sa-visually-hidden"><table aria-label={`${metric.label} by ${unit}: ${metric.format(currentTotal)} this period${previousTotal != null ? `, ${metric.format(previousTotal)} in the comparison period` : ""}`}>
      <thead><tr><th scope="col">Date</th><th scope="col">This period</th>{previous && <th scope="col">Comparison date</th>}{previous && <th scope="col">Comparison</th>}</tr></thead>
      <tbody>{current.map((point, index) => <tr key={point.start_date}>
        <th scope="row">{periodLabel(point, granularity)}{point.holiday && ` (${point.holiday})`}</th>
        <td>{metric.format(metric.value(point))}</td>
        {previous && <td>{periodLabel(previous[index], granularity) || "—"}{previous[index]?.holiday && ` (${previous[index].holiday})`}</td>}
        {previous && <td>{previous[index] ? metric.format(metric.value(previous[index])) : "—"}</td>}
      </tr>)}</tbody>
    </table></div>
  </section>;
}
